import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  of,
  switchMap,
} from 'rxjs';

import { PackageSelectItemDto } from '@app/api/models';
import {
  ContainerSortimentsService,
  PackageSortimentsService,
  ProductsService,
} from '@app/api/services';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';

import { ProductCollectionsActions } from './product-collections.slice';

@Injectable({ providedIn: 'root' })
export class ProductCollectionsEffects {
  loadPackagingUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCollectionsActions.loadPackagingUnitsBySubtype.trigger),
      filter((action) => !!action.packingMethodSubType),
      concatMap((action) => {
        if (action.withDepositFee) {
          return this.containerSortimentsService
            .getContainerSelectItemsFromSortiment({
              subType: action.packingMethodSubType,
            })
            .pipe(
              map((res: Array<PackageSelectItemDto>) =>
                ProductCollectionsActions.loadPackagingUnitsBySubtype.success({
                  withDepositFee: action.withDepositFee,
                  packingMethodSubType: action.packingMethodSubType,
                  packagingUnits: res,
                })
              )
            );
        } else {
          return this.packageSortimentsService
            .getPackageSelectItemsFromSortiment({
              subType: action.packingMethodSubType,
            })
            .pipe(
              map((res: Array<PackageSelectItemDto>) =>
                ProductCollectionsActions.loadPackagingUnitsBySubtype.success({
                  withDepositFee: action.withDepositFee,
                  packingMethodSubType: action.packingMethodSubType,
                  packagingUnits: res,
                })
              )
            );
        }
      })
    )
  );

  loadCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCollectionsActions.loadCollections.trigger),
      switchMap((action) =>
        this.productsService
          .getCollectionPackagingUnitHierarchies({
            packagingUnitId: action.packagingUnitId,
          })
          .pipe(
            map((response) => {
              return ProductCollectionsActions.loadCollections.success({
                collections: response,
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(ProductCollectionsActions.loadCollections.failure(error.error))
            )
          )
      )
    )
  );

  saveCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCollectionsActions.saveCollection.trigger),
      exhaustMap((action) =>
        (action.collectionPackagingUnitId
          ? this.productsService.updateCollectionPackagingUnit({
              packagingUnitId: action.packagingUnitId,
              collectionPackagingUnitId: action.collectionPackagingUnitId,
              body: action.collectionLevel,
            })
          : this.productsService.createCollectionPackagingUnit({
              packagingUnitId: action.packagingUnitId,
              body: action.collectionLevel,
            })
        ).pipe(
          map((response) => {
            if (action.collectionLevel.packingMethodSubType) {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 5000,
                data: this.translateService.instant(
                  'PRODUCTS.PRODUCT_VARIANT.SAVE_SUCCESS'
                ),
              });
            }
            return ProductCollectionsActions.saveCollection.success({
              collectionLevel: response ?? action.collectionLevel,
              collectionGroupIndex: action.collectionGroupIndex,
              created: !('id' in action.collectionLevel),
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(ProductCollectionsActions.saveCollection.failure(error.error))
          )
        )
      )
    )
  );

  deleteCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCollectionsActions.deleteCollection.trigger),
      exhaustMap((action) =>
        this.productsService
          .deleteCollectionPackagingUnit({
            packagingUnitId: action.packagingUnitId,
            collectionPackagingUnitId: action.collectionLevelId,
          })
          .pipe(
            map(() =>
              ProductCollectionsActions.deleteCollection.success({
                collectionLevelId: action.collectionLevelId,
              })
            ),
            catchError((error: HttpErrorResponse) =>
              of(ProductCollectionsActions.saveCollection.failure(error.error))
            )
          )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService,
    private readonly productsService: ProductsService,
    private readonly containerSortimentsService: ContainerSortimentsService,
    private readonly packageSortimentsService: PackageSortimentsService
  ) {}
}
